import { I18n } from '@affine/i18n';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { scrollbarStyle } from '@blocksuite/affine/shared/styles';
import { unsafeCSSVarV2 } from '@blocksuite/affine/shared/theme';
import { type EditorHost } from '@blocksuite/affine/std';
import { InformationIcon, ToggleDownIcon } from '@blocksuite/icons/lit';
import { signal } from '@preact/signals-core';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { baseTheme } from '@streamlain/theme';
import { css, html, LitElement, nothing, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';

import { SUPPORT_EMAIL } from '../../../modules/streamlain/contact';
import {
  type AIError,
  AIProvider,
  BudgetExceededError,
  InsufficientCreditsError,
  PaymentRequiredError,
  QuotaExhaustedError,
  UnauthorizedError,
} from '../provider';

export class AIErrorWrapper extends SignalWatcher(WithDisposable(LitElement)) {
  static override styles = css`
    .error-wrapper {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      gap: 8px;
      align-self: stretch;
      border-radius: 4px;
      padding: 8px 8px 12px 8px;
      background-color: ${unsafeCSSVarV2('aI/errorBackground')};
      font-family: ${unsafeCSS(baseTheme.fontSansFamily)};

      .content {
        align-items: flex-start;
        display: flex;
        gap: 8px;
        align-self: stretch;
        color: ${unsafeCSSVarV2('aI/errorText')};
        font-feature-settings:
          'clig' off,
          'liga' off;
        /* light/sm */
        font-size: var(--streamlain-font-sm);
        font-style: normal;
        font-weight: 400;
        line-height: 22px; /* 157.143% */

        .icon svg {
          position: relative;
          top: 3px;
        }
      }

      .text-container {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .detail-container {
        display: flex;
        flex-direction: column;
        gap: 4px;
        width: 100%;
      }
      .detail-title {
        display: flex;
        align-items: center;
      }
      .detail-title:hover {
        cursor: pointer;
      }
      .detail-content {
        padding: 8px;
        border-radius: 4px;
        background-color: ${unsafeCSSVarV2('aI/errorDetailBackground')};
        overflow: auto;
      }
      ${scrollbarStyle('.detail-content')}

      .toggle {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .toggle.up svg {
        transform: rotate(180deg);
        transition: all 0.2s ease-in-out;
      }

      .action {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        width: 100%;
      }
      .action-button {
        cursor: pointer;
        color: ${unsafeCSSVarV2('text/primary')};
        background: ${unsafeCSSVarV2('button/secondary')};
        border-radius: 8px;
        border: 1px solid ${unsafeCSSVarV2('button/innerBlackBorder')};
        padding: 4px 12px;
        font-size: var(--streamlain-font-xs);
        font-style: normal;
        font-weight: 500;
        line-height: 20px;
      }
      .action-button:hover {
        transition: all 0.2s ease-in-out;
        background-image: linear-gradient(
          rgba(0, 0, 0, 0.04),
          rgba(0, 0, 0, 0.04)
        );
      }
    }
  `;

  private readonly _showDetailContent = signal(false);

  protected override render() {
    return html` <div class="error-wrapper">
      <div class="content">
        <div class="icon">${InformationIcon()}</div>
        <div class="text-container">
          <div>${this.text}</div>
          ${this.showDetailPanel
            ? html`<div class="detail-container">
                <div
                  class="detail-title"
                  @click=${() =>
                    (this._showDetailContent.value =
                      !this._showDetailContent.value)}
                >
                  <span>Show detail</span>
                  <span
                    class="toggle ${this._showDetailContent.value
                      ? 'down'
                      : 'up'}"
                  >
                    ${ToggleDownIcon({ width: '16px', height: '16px' })}
                  </span>
                </div>
                ${this._showDetailContent.value
                  ? html`<div class="detail-content">${this.errorMessage}</div>`
                  : nothing}
              </div>`
            : nothing}
        </div>
      </div>
      <div class="action">
        ${this.actionText
          ? html`<span
              class="action-button"
              @click=${this.onClick}
              data-testid="ai-error-action-button"
            >
              ${this.actionText}
              ${this.actionTooltip
                ? html`<affine-tooltip tip-position="top">
                    ${this.actionTooltip}
                  </affine-tooltip>`
                : nothing}
            </span>`
          : nothing}
      </div>
    </div>`;
  }

  @property({ attribute: false })
  accessor text: string = '';

  @property({ attribute: false })
  accessor onClick: () => void = () => {};

  @property({ attribute: false })
  accessor errorMessage: string = '';

  @property({ attribute: false })
  accessor actionText: string = 'Contact us';

  @property({ attribute: false })
  accessor actionTooltip: string = '';

  @property({ attribute: false })
  accessor showDetailPanel: boolean = false;

  @property({ attribute: 'data-testid', reflect: true })
  accessor testId = 'ai-error';
}

const PaymentRequiredErrorRenderer = (host?: EditorHost | null) => html`
  <ai-error-wrapper
    .text=${`You've reached the current usage cap for ${BRAND_DISPLAY_NAME} AI. You can subscribe to ${BRAND_DISPLAY_NAME} AI to continue the AI experience!`}
    .actionText=${'Upgrade'}
    .onClick=${() => AIProvider.slots.requestUpgradePlan.next({ host })}
  ></ai-error-wrapper>
`;

const LoginRequiredErrorRenderer = (host?: EditorHost | null) => html`
  <ai-error-wrapper
    .text=${`You need to login to ${BRAND_DISPLAY_NAME} Cloud to continue using ${BRAND_DISPLAY_NAME} AI.`}
    .actionText=${'Login'}
    .onClick=${() => AIProvider.slots.requestLogin.next({ host })}
  ></ai-error-wrapper>
`;

type ErrorProps = {
  text?: string;
  errorMessage?: string;
  actionText?: string;
  actionTooltip?: string;
};

const generalErrorText =
  'An error occurred. If this issue persists, please let us know.';

const GeneralErrorRenderer = (props: ErrorProps = {}) => {
  const onClick = () => {
    window.open(`mailto:${SUPPORT_EMAIL}`, '_blank');
  };

  return html`<ai-error-wrapper
    .text=${props.text ?? generalErrorText}
    .errorMessage=${props.errorMessage ?? ''}
    .showDetailPanel=${!!props.errorMessage}
    .actionText=${props.actionText ?? 'Contact us'}
    .actionTooltip=${props.actionTooltip ?? SUPPORT_EMAIL}
    .onClick=${onClick}
  ></ai-error-wrapper>`;
};

const quotaExhaustedMessages: Record<string, string> = {
  user: 'Your API key has run out of quota. Please top up your account with the API provider.',
  workspace:
    'The workspace API key has run out of quota. Your workspace admin has been notified.',
  server:
    'Deine monatlichen Credits sind aufgebraucht. Du kannst in den Workspace-Einstellungen weitere Credits erwerben.',
};

function tWithFallback(
  key: string,
  fallback: string,
  options?: Record<string, string>
): string {
  const value = I18n.t(key, options);
  if (value !== key) return value;
  return Object.entries(options ?? {}).reduce(
    (text, [name, replacement]) => text.replaceAll(`{{${name}}}`, replacement),
    fallback
  );
}

const QuotaExhaustedErrorRenderer = (error: QuotaExhaustedError) => {
  const text =
    quotaExhaustedMessages[error.keySource] ?? quotaExhaustedMessages.server;

  const isServerQuota = error.keySource === 'server';

  return html`<ai-error-wrapper
    .text=${text}
    .errorMessage=${isServerQuota ? '' : error.message}
    .showDetailPanel=${!isServerQuota}
    .actionText=${isServerQuota ? 'Open Settings' : 'Contact us'}
    .actionTooltip=${isServerQuota ? '' : SUPPORT_EMAIL}
    .onClick=${isServerQuota
      ? () => AIProvider.slots.requestUpgradePlan.next({ host: null })
      : () => window.open(`mailto:${SUPPORT_EMAIL}`, '_blank')}
  ></ai-error-wrapper>`;
};

// sl-ufq6.1.3: Insufficient credits — role-differentiated via onOpenSettings proxy
const InsufficientCreditsRenderer = (
  onOpenSettings?: (() => void) | undefined
) => {
  const isAdmin = !!onOpenSettings;

  if (isAdmin) {
    return html`<ai-error-wrapper
      .text=${I18n.t('com.streamlain.ai.exhausted.admin')}
      .errorMessage=${''}
      .showDetailPanel=${false}
      .actionText=${I18n.t('com.streamlain.ai.exhausted.action')}
      .actionTooltip=${I18n.t('com.streamlain.ai.exhausted.action-tooltip')}
      .onClick=${() => onOpenSettings()}
    ></ai-error-wrapper>`;
  }

  // Member: neutral text, no action button
  return html`<ai-error-wrapper
    .text=${I18n.t('com.streamlain.ai.exhausted.member')}
    .errorMessage=${''}
    .showDetailPanel=${false}
  ></ai-error-wrapper>`;
};

// sl-ufq6.4.1: Budget limit exceeded — differentiated by error code
const BudgetExceededRenderer = (error: BudgetExceededError) => {
  const resetAtFormatted = error.resetAt
    ? new Date(error.resetAt).toLocaleTimeString()
    : '';

  const messageKey =
    error.code === 'MANAGED_AI_ACCESS_DENIED'
      ? 'com.streamlain.ai.limit.access-not-assigned'
      : error.code === 'USER_BUDGET_EXCEEDED'
        ? 'com.streamlain.ai.limit.user-reached'
        : error.code === 'AUTOMATION_BUDGET_EXCEEDED'
          ? 'com.streamlain.ai.limit.automation-reached'
          : 'com.streamlain.ai.limit.group-reached';

  return html`<ai-error-wrapper
    .text=${I18n.t(messageKey)}
    .errorMessage=${error.code === 'MANAGED_AI_ACCESS_DENIED'
      ? I18n.t('com.streamlain.ai.limit.contact-admin')
      : resetAtFormatted
        ? `Reset: ${resetAtFormatted}`
        : I18n.t('com.streamlain.ai.limit.contact-admin')}
    .showDetailPanel=${true}
  ></ai-error-wrapper>`;
};

export function AIChatErrorRenderer(
  error: AIError,
  host?: EditorHost | null,
  onOpenSettings?: (() => void) | undefined
) {
  if (error instanceof BudgetExceededError) {
    return BudgetExceededRenderer(error);
  } else if (error instanceof InsufficientCreditsError) {
    return InsufficientCreditsRenderer(onOpenSettings);
  } else if (error instanceof QuotaExhaustedError) {
    return QuotaExhaustedErrorRenderer(error);
  } else if (error instanceof PaymentRequiredError) {
    return PaymentRequiredErrorRenderer(host);
  } else if (error instanceof UnauthorizedError) {
    return LoginRequiredErrorRenderer(host);
  } else {
    const readableError = getReadableGeneralError(error.message);
    return GeneralErrorRenderer({
      text: readableError.text,
      errorMessage: readableError.detail,
      actionText: readableError.actionText,
    });
  }
}

export function getReadableGeneralError(message: string): {
  text?: string;
  detail: string;
  actionText?: string;
} {
  if (isNetworkUnavailableError(message)) {
    return {
      text: tWithFallback(
        'com.streamlain.ai.offline.unavailable',
        `${BRAND_DISPLAY_NAME} AI is not available without a server connection.`
      ),
      detail: tWithFallback(
        'com.streamlain.ai.offline.detail',
        `Keep working locally. ${BRAND_DISPLAY_NAME} AI can be used again after the server connection is restored.`
      ),
      actionText: '',
    };
  }

  if (isModelUnavailableError(message)) {
    const model = extractUnavailableModelName(message);
    return {
      text: model
        ? tWithFallback(
            'com.streamlain.ai.model.unavailable.named',
            '{{model}} is not available for the current AI account. Choose another model in the selector below.',
            { model }
          )
        : tWithFallback(
            'com.streamlain.ai.model.unavailable.selected',
            'The selected AI model is not available for the current account. Choose another model in the selector below.'
          ),
      detail: tWithFallback(
        'com.streamlain.ai.model.unavailable.detail',
        'The AI provider rejected this model for the configured account or region.'
      ),
      actionText: '',
    };
  }

  if (isWorkspaceAIUnavailableError(message)) {
    const lower = message.toLowerCase();
    const isDecryptionFailure =
      lower.includes('unsupported state or unable to authenticate data') ||
      lower.includes('cannot be decrypted') ||
      lower.includes('ai_configuration_unavailable');
    return {
      text: tWithFallback(
        'com.streamlain.ai.workspace.unavailable',
        `${BRAND_DISPLAY_NAME} AI is not available in this workspace.`
      ),
      detail: isDecryptionFailure
        ? tWithFallback(
            'com.streamlain.ai.workspace.unavailable.decrypt-detail',
            'The workspace AI key configuration cannot be decrypted. Contact an administrator.'
          )
        : tWithFallback(
            'com.streamlain.ai.workspace.unavailable.detail',
            'No usable chat model is configured for this workspace. Check the AI settings or contact a workspace administrator.'
          ),
      actionText: '',
    };
  }

  return { detail: sanitizeErrorDetail(message) };
}

function isNetworkUnavailableError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('network_error') ||
    lower.includes('failed to fetch') ||
    lower.includes('network error') ||
    lower.includes('load failed') ||
    lower.includes('networkerror when attempting to fetch resource')
  );
}

function isWorkspaceAIUnavailableError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('ai_model_unavailable') ||
    lower.includes('ai_unavailable') ||
    lower.includes('ai_plan_unavailable') ||
    lower.includes('ai_disabled') ||
    lower.includes('ai_suspended') ||
    lower.includes('ai_configuration_unavailable') ||
    lower.includes('no default chat model configured') ||
    lower.includes('no chat model is configured') ||
    lower.includes('streamlain ai is not available in this workspace') ||
    lower.includes('streamlain ai is not available for this workspace') ||
    lower.includes('workspace ai key configuration cannot be decrypted') ||
    lower.includes('no visible chat model configured') ||
    lower.includes('unsupported state or unable to authenticate data')
  );
}

function isModelUnavailableError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('not available for this account') ||
    lower.includes('model not found')
  );
}

function extractUnavailableModelName(message: string): string {
  const match =
    message.match(/(?:^|\s)(?:\d{3}:\s*)?([a-z0-9_.-]+)\s+is not available/i) ??
    message.match(/model not found:\s*([a-z0-9_.-]+)/i);
  return match?.[1] ? humanizeModelId(match[1]) : '';
}

function humanizeModelId(modelId: string): string {
  const id = (modelId.split('.').pop() ?? modelId).replace(
    /-(\d)-(\d)(?=-|$)/gu,
    '-$1.$2'
  );
  return id
    .replace(/-\d{8}$/u, '')
    .split('-')
    .filter(Boolean)
    .map(part => {
      if (/^\d+(?:\.\d+)*$/u.test(part)) return part;
      if (part.toLowerCase() === 'gpt') return 'GPT';
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ');
}

function sanitizeErrorDetail(message: string): string {
  return message
    .replace(/https?:\/\/\S+/gu, '')
    .replace(/\n?Identify:\s*(?:undefined|null)?/giu, '')
    .replace(/\s+/gu, ' ')
    .trim();
}

declare global {
  interface HTMLElementTagNameMap {
    'ai-error-wrapper': AIErrorWrapper;
  }
}
